import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiCall } from '../utils/api';
import { toast } from './ui/sonner';
import {
  Shield, Users, Stethoscope, Phone, Search, X, Plus, Loader2, Save,
  AlertTriangle, ChevronDown, Info, Building2,
} from 'lucide-react';

const ROLE_META = {
  hr:         { label: 'HR',         icon: Shield,      tint: 'from-rose-500 to-pink-500',     bg: 'bg-rose-50',     text: 'text-rose-700' },
  supervisor: { label: 'Supervisor', icon: Users,       tint: 'from-indigo-500 to-blue-500',   bg: 'bg-indigo-50',   text: 'text-indigo-700' },
  on_call:    { label: 'On-Call',    icon: Phone,       tint: 'from-emerald-500 to-teal-500',  bg: 'bg-emerald-50',  text: 'text-emerald-700' },
};

function MemberPicker({ members, selectedIds, onAdd, role }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return members
      .filter((m) => !selectedIds.includes(m._id))
      .filter((m) => !s || `${m.full_name} ${m.email} ${m.role}`.toLowerCase().includes(s))
      .slice(0, 12);
  }, [members, selectedIds, q]);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-lg text-sm text-slate-600 hover:border-slate-400 hover:bg-slate-50 transition"
        data-testid={`add-${role}-btn`}
      >
        <span className="inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add {ROLE_META[role].label.toLowerCase()}</span>
        <ChevronDown className={`w-4 h-4 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
          <div className="p-2 border-b border-slate-100 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search team members..."
              className="w-full pl-8 pr-2 py-1.5 text-sm border border-slate-200 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              data-testid={`search-${role}`}
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-6">No members match.</p>
            ) : (
              filtered.map((m) => (
                <button
                  key={m._id}
                  onClick={() => { onAdd(m._id); setOpen(false); setQ(''); }}
                  className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-slate-50"
                  data-testid={`pick-${role}-${m._id}`}
                >
                  <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-xs font-bold">
                    {m.full_name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{m.full_name}</p>
                    <p className="text-[11px] text-slate-500 truncate">{m.email} · <span className="capitalize">{m.role}</span></p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function RecipientChip({ user, onRemove, color }) {
  if (!user) return null;
  return (
    <div className={`flex items-center gap-2 pl-2 pr-1 py-1 rounded-full ${color.bg} ring-1 ring-inset ring-slate-200`} data-testid={`chip-${user._id}`}>
      <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${color.tint} text-white flex items-center justify-center text-[10px] font-bold`}>
        {user.full_name?.[0]?.toUpperCase() || '?'}
      </div>
      <div className="min-w-0">
        <p className={`text-xs font-semibold ${color.text} truncate max-w-[140px]`}>{user.full_name}</p>
      </div>
      {onRemove && (
        <button onClick={onRemove} className="p-0.5 hover:bg-white/60 rounded-full" title="Remove">
          <X className={`w-3.5 h-3.5 ${color.text}`} />
        </button>
      )}
    </div>
  );
}

export default function ReportRecipientsManager({ orgId = null, viewLabel = null, readOnly = false, onSaved = null }) {
  /**
   * orgId — when null: load own org via /api/employer/report-recipients
   *         when set: admin-on-behalf-of via /api/admin/report-recipients/{orgId}
   * viewLabel — optional org name displayed when admin manages on behalf
   */
  const [recipients, setRecipients] = useState({ hr: [], supervisor: [], on_call: [], notes: '' });
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isAdminMode = !!orgId;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isAdminMode) {
        const res = await apiCall(`/api/admin/report-recipients/${orgId}`);
        setRecipients(res.recipients || { hr: [], supervisor: [], on_call: [], notes: '' });
        setMembers(res.members || []);
      } else {
        const [r1, r2] = await Promise.all([
          apiCall('/api/employer/report-recipients'),
          apiCall('/api/employer/team-members'),
        ]);
        setRecipients(r1.recipients || { hr: [], supervisor: [], on_call: [], notes: '' });
        setMembers(r2.members || []);
      }
    } catch (e) {
      toast.error('Failed to load recipients', { description: e.message });
    }
    setLoading(false);
  }, [orgId, isAdminMode]);

  useEffect(() => { load(); }, [load]);

  const addRecipient = (role, userId) => {
    const member = members.find((m) => m._id === userId);
    if (!member) return;
    setRecipients((prev) => {
      if ((prev[role] || []).some((u) => u._id === userId)) return prev;
      return { ...prev, [role]: [...(prev[role] || []), member] };
    });
  };

  const removeRecipient = (role, userId) => {
    setRecipients((prev) => ({ ...prev, [role]: (prev[role] || []).filter((u) => u._id !== userId) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        hr: recipients.hr.map((u) => u._id),
        supervisor: recipients.supervisor.map((u) => u._id),
        on_call: recipients.on_call.map((u) => u._id),
        notes: recipients.notes || '',
      };
      const url = isAdminMode ? `/api/admin/report-recipients/${orgId}` : '/api/employer/report-recipients';
      const res = await apiCall(url, { method: 'PUT', body: JSON.stringify(payload) });
      setRecipients(res.recipients);
      toast.success('Report recipients saved');
      if (onSaved) onSaved();
    } catch (e) {
      toast.error('Save failed', { description: e.message });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-12 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  const totalRecipients = (recipients.hr?.length || 0) + (recipients.supervisor?.length || 0) + (recipients.on_call?.length || 0);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="report-recipients-manager">
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-600 via-orange-600 to-amber-500 p-5 text-white">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-white/15 backdrop-blur-sm ring-1 ring-white/20">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold tracking-tight">Report Recipients</h3>
              <p className="text-xs text-white/85 mt-0.5">Workers assigned the HR, Supervisor, or On-Call role for high-priority incident reports.</p>
              {viewLabel && (
                <p className="text-[11px] mt-1 inline-flex items-center gap-1 bg-white/15 px-2 py-0.5 rounded-full"><Building2 className="w-3 h-3" /> {viewLabel}</p>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-white/70 uppercase tracking-wider">Recipients</p>
            <p className="text-3xl font-bold leading-none">{totalRecipients}</p>
          </div>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-amber-50 border-b border-amber-100 px-5 py-2.5 flex items-start gap-2 text-xs text-amber-900">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <p>When a worker submits a report (criminal activity, safety, harassment, etc.), every person below receives an instant high-priority push + in-app alert. Platform admins are always notified — no setup needed.</p>
      </div>

      {/* Role rows */}
      <div className="p-5 space-y-5">
        {(['hr', 'supervisor', 'on_call']).map((role) => {
          const meta = ROLE_META[role];
          const Icon = meta.icon;
          const list = recipients[role] || [];
          return (
            <div key={role} className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                  <span className={`p-1 rounded-md bg-gradient-to-br ${meta.tint} text-white`}><Icon className="w-3.5 h-3.5" /></span>
                  {meta.label}
                  <span className="text-xs font-semibold text-slate-400">({list.length})</span>
                </h4>
                {list.length === 0 && (
                  <span className="text-[11px] text-rose-600 font-medium inline-flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" /> No {meta.label.toLowerCase()} set</span>
                )}
              </div>
              {list.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {list.map((u) => (
                    <RecipientChip key={u._id} user={u} onRemove={readOnly ? null : () => removeRecipient(role, u._id)} color={meta} />
                  ))}
                </div>
              )}
              {!readOnly && (
                <MemberPicker
                  members={members}
                  selectedIds={list.map((u) => u._id)}
                  onAdd={(uid) => addRecipient(role, uid)}
                  role={role}
                />
              )}
            </div>
          );
        })}

        {/* Notes */}
        <div className="space-y-1">
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Internal notes <span className="text-slate-400 font-normal normal-case">(optional)</span></label>
          <textarea
            rows={2}
            value={recipients.notes || ''}
            onChange={(e) => setRecipients((p) => ({ ...p, notes: e.target.value }))}
            disabled={readOnly}
            placeholder="e.g. After-hours escalation pages on-call only"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500 disabled:bg-slate-50"
            data-testid="report-recipients-notes"
          />
        </div>
      </div>

      {/* Footer */}
      {!readOnly && (
        <div className="border-t border-slate-100 bg-slate-50 px-5 py-3 flex items-center justify-between gap-2">
          <p className="text-[11px] text-slate-500">
            {totalRecipients === 0
              ? 'Add at least one recipient per role to ensure full coverage.'
              : `${totalRecipients} ${totalRecipients === 1 ? 'recipient' : 'recipients'} will be paged on every report.`}
          </p>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 bg-gradient-to-r from-rose-600 to-orange-600 hover:from-rose-700 hover:to-orange-700 text-white rounded-lg text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50 shadow-sm"
            data-testid="save-recipients-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Recipients
          </button>
        </div>
      )}
    </div>
  );
}
